/**
 * Production-Ready Application
 * 
 * Phase 4: Final integration with performance optimizations,
 * error boundaries, and complete architecture consolidation.
 */

import { Suspense, lazy, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import RoleGuard from '@/components/RoleGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Layout } from '@/components/Layout';

// Phase 4 Providers
import { AppStateProvider, ErrorDisplay, LoadingOverlay } from '@/shared/AppStateProvider';
import { ModalProvider } from '@/shared/ModalProvider';

// Performance Optimizations  
import { trackWebVitals } from '@/utils/webVitals';

// Create optimized QueryClient with better defaults
const createQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always'
    },
    mutations: {
      retry: 1
    }
  }
});

// Optimized lazy loading with correct named exports
const Overview = lazy(() => import('./pages/Overview'));

const Dashboards = lazy(() => import('./pages/Dashboards'));

const DataSubscriptions = lazy(() => 
  import('./pages/DataSubscriptions').then(m => ({ default: m.DataSubscriptions }))
);

const DataModels = lazy(() => import('./pages/DataModels'));

const Governance = lazy(() => import('./pages/Governance'));

const Learn = lazy(() => 
  import('./pages/Learn').then(m => ({ default: m.Learn }))
);

const Support = lazy(() => 
  import('./pages/Support').then(m => ({ default: m.Support }))
);

const Chat = lazy(() => 
  import('./pages/Chat').then(m => ({ default: m.Chat }))
);

const Administration = lazy(() => import('./pages/Administration'));

const Auth = lazy(() => import('./pages/Auth'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Memoized loading component for better performance
const AppLoadingFallback = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <p className="text-sm text-muted-foreground">Loading application...</p>
    </div>
  </div>
));

// Memoized page loading component
const PageLoadingFallback = memo(() => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      <p className="text-sm text-muted-foreground">Loading page...</p>
    </div>
  </div>
));

// Main App component with full integration
const ProductionApp = memo(() => {
  const queryClient = createQueryClient();

  // Initialize web vitals reporting in production
  if (import.meta.env.PROD) {
    trackWebVitals();
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <AppStateProvider>
              <ModalProvider>
                <Router>
                  <div className="min-h-screen bg-background font-sans antialiased">
                    <Suspense fallback={<AppLoadingFallback />}>
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/auth" element={<Auth />} />
                        
                        {/* Protected Routes with Layout */}
                        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                          <Route index element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <Overview />
                            </Suspense>
                          } />
                          <Route 
                            path="/overview" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Overview />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/dashboards" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Dashboards />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/data-subscriptions" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <DataSubscriptions />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/data-models" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <DataModels />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/governance" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Governance />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/learn" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Learn />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/support" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Support />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/chat" 
                            element={
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Chat />
                              </Suspense>
                            } 
                          />
                          <Route 
                            path="/admin" 
                            element={
                              <RoleGuard requiredRole="admin">
                                <Suspense fallback={<PageLoadingFallback />}>
                                  <Administration />
                                </Suspense>
                              </RoleGuard>
                            } 
                          />
                        </Route>
                        
                        {/* 404 Route */}
                        <Route 
                          path="*" 
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <NotFound />
                            </Suspense>
                          } 
                        />
                      </Routes>
                    </Suspense>
                    
                    {/* Global Components */}
                    <ErrorDisplay />
                    <LoadingOverlay />
                    <Toaster position="top-right" />
                  </div>
                </Router>
              </ModalProvider>
            </AppStateProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
});

ProductionApp.displayName = 'ProductionApp';

export default ProductionApp;